// Vue
import { ref } from "vue";

// Dependencies
import { defineStore } from "pinia";

// Types
import { LocalModelType } from "@/features/BuildingProject";

// Providers
import { useDefaultModels } from "@/dataProviders/DefaultModels";

const useModels = defineStore("ModelsProvider", () => {
  // ///////////////////
  // Providers
  // ///////////////////

  const defaultModels = useDefaultModels();

  // ///////////////////
  // Refs
  // ///////////////////

  const models = ref<Array<LocalModelType>>([]);

  // ///////////////////
  // Getters
  // ///////////////////

  const getModels = () => {
    return models.value;
  };

  const getActiveModels = () => {
    return models.value.filter((model) => model.isActive === true);
  };
  const getInactiveModels = () => {
    return models.value.filter((model) => model.isActive === false);
  };

  // ///////////////////
  // Setters
  // //////////////////

  const setDefaultModels = () => {
    setModels(
      defaultModels.getDefaultModels().map((model) => {
        // Format name
        const name = model.name.toLowerCase();

        // Is there a local version of this model?
        const localModel = models.value.find((localModel) => localModel.name === name);

        // Return the local model if it exists, otherwise return a new model
        return localModel
          ? localModel
          : {
              name,
              enumValue: model.id,
              isActive: false,
            };
      })
    );
  };

  const setModels = (newModels: Array<LocalModelType>) => {
    models.value = newModels;
  };

  // ///////////////////
  // Methods
  // //////////////////

  const addModel = (model: LocalModelType) => {
    if (models.value.find((localModel) => localModel.name === model.name)) return;
    model.name = model.name.toLowerCase();
    models.value.push(model);
  };

  // Flips the store's own model, not the incoming one — `model` is the same reactive object
  // found by name below, so copying its isActive across was a self-assignment no-op.
  const toggleModel = (model: LocalModelType) => {
    const foundLocalModel = models.value.find((localModel) => localModel.name === model.name);
    if (foundLocalModel) foundLocalModel.isActive = !foundLocalModel.isActive;
  };

  // Select all / Clear on the checklist: every listed model, in place, so the objects the list holds stay the same.
  const setAllActive = (active: boolean) => {
    models.value.forEach((localModel) => {
      localModel.isActive = active;
    });
  };

  // ///////////////////
  // Watchers
  // ///////////////////

  return {
    setDefaultModels,
    getActiveModels,
    getInactiveModels,
    toggleModel,
    setAllActive,
    addModel,
    getModels,
    setModels,
  };
});

export default useModels;
